import { useMutation, useQueryClient } from '@tanstack/react-query';
import snakecaseKeys from 'snakecase-keys';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { promiseAllThrottled } from '~/utils/promise';

import { queryKeysUser } from './queryKeys';
import { updateUserSettings } from './useMutationUpdateUserSettings';

// `firstName`/`lastName` are both-or-neither: `updateUserName` always writes
// both query params, so a lone `firstName` would silently blank `lastName`
// (and vice versa). The intersection makes that combination unrepresentable
// at the type level instead of relying on callers to remember to pair them.
type UserDetailsData = {
  contactInformation?: string;
  position?: string;
} & (
  | { firstName?: undefined; lastName?: undefined }
  | { firstName: string; lastName: string }
);

/**
 * Updates user name.
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/set_user_name_user_name_put
 */
const updateUserName = async (firstName?: string, lastName?: string) => {
  const searchParams = snakecaseKeys({
    firstName: firstName ?? '',
    lastName: lastName ?? '',
  });

  await vestigasApi.put(ENDPOINT.USER.UPDATE_NAME(), {
    searchParams: searchParams,
  });
};

/**
 * Updates user position.
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/set_user_position_user_position_put
 */
const updateUserPosition = async (position?: string) => {
  // `undefined` means position is not part of this update; an explicit empty
  // string must still reach the API so clearing the field works.
  if (position === undefined) {
    return;
  }

  await vestigasApi.put(ENDPOINT.USER.UPDATE_POSITION(), {
    searchParams: { position },
  });
};

/**
 * Updates user details by making separate API calls for name, position and phone number.
 *
 * @param {UserDetailsData} userDetails - The user details to update
 * @returns {Promise<void>} A promise that resolves when all updates are complete
 */
export const updateUserDetails = async (
  userId: UUID,
  userDetails: UserDetailsData,
) => {
  const { contactInformation, firstName, lastName, position } = userDetails;
  const hasNameUpdate = firstName !== undefined || lastName !== undefined;
  const hasSettingsUpdate =
    contactInformation !== undefined || position !== undefined;

  await promiseAllThrottled<unknown>([
    hasNameUpdate ? updateUserName(firstName, lastName) : Promise.resolve(),
    updateUserPosition(position),
    // The backend stores position twice (`user.position` column and
    // `settings["position"]`) and /user/data falls back to the settings copy
    // when the column is empty — mirror it here so clearing actually sticks.
    // `undefined` is dropped by JSON serialization and leaves the key alone.
    hasSettingsUpdate
      ? updateUserSettings(userId, { contactInformation, position })
      : Promise.resolve(),
  ]);
};

/**
 * Custom hook for updating user details.
 * Combines separate API calls for updating name, position and phone number.
 *
 * @returns {Object} An object containing the mutation function and related properties
 */
export const useMutationUpdateUserDetails = (
  options?: Parameters<typeof useMutation>[0],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userDetails,
      userId,
    }: {
      userDetails: UserDetailsData;
      userId: UUID;
    }) => updateUserDetails(userId, userDetails),
    onSuccess: async (_, { userId }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysUser.get(userId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeysUser.getData(true),
      });
    },
    ...options,
  });
};
